import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Stack from '@mui/joy/Stack';
import Alert from '@mui/joy/Alert';
import Table from '@mui/joy/Table';
import IconButton from '@mui/joy/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { categoriesApi } from '../../api/categories.api';
import { CreateCategoryDto } from '../../types/category';

const emptyCategory = (): CreateCategoryDto => ({ name: '', description: '' });

const BulkCategoryCreator: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CreateCategoryDto[]>(() => [emptyCategory()]);
  const [results, setResults] = useState<{ success: number; errors: number }>({
    success: 0,
    errors: 0,
  });
  const [showResults, setShowResults] = useState(false);

  const updateCategory = useCallback((index: number, patch: Partial<CreateCategoryDto>) => {
    setCategories((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const handleAddRow = () => {
    setCategories((prev) => [...prev, emptyCategory()]);
  };

  const handleRemoveCategory = (index: number) => {
    setCategories((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validCategories = categories.filter((cat) => cat.name.trim() !== '');
    if (validCategories.length === 0) {
      alert('Por favor, ingresa al menos una categoría válida.');
      return;
    }
    setLoading(true);
    setShowResults(false);
    let successCount = 0;
    let errorCount = 0;
    for (const category of validCategories) {
      try {
        await categoriesApi.create(category);
        successCount++;
      } catch (error) {
        console.error('Error al crear categoría:', error);
        errorCount++;
      }
    }
    setResults({ success: successCount, errors: errorCount });
    setShowResults(true);
    setLoading(false);
  };

  const handleImportFromCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const content = event.target.result.toString();
        const lines = content.split('\n').filter((l) => l.trim());
        const dataLines = lines.length > 1 && /^nombre/i.test(lines[0].split(',')[0]?.trim() ?? '')
          ? lines.slice(1)
          : lines;
        const importedCategories: CreateCategoryDto[] = [];
        dataLines.forEach((line) => {
          const [name, description = ''] = line.split(',').map((item) => item.trim());
          if (name) {
            importedCategories.push({ name, description });
          }
        });
        if (importedCategories.length > 0) {
          setCategories(importedCategories);
        } else {
          alert('No se encontraron categorías válidas en el archivo.');
        }
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const csvContent =
      'Nombre,Descripción\nEjemplo Categoría 1,Descripción de ejemplo 1\nEjemplo Categoría 2,Descripción de ejemplo 2';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'plantilla_categorias.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const bulkTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: 560,
    '& thead th': {
      bgcolor: 'background.surface',
      zIndex: 1,
      whiteSpace: 'nowrap',
      fontWeight: 'lg',
      fontSize: 'xs',
      py: 0.5,
      px: 1,
      verticalAlign: 'middle',
    },
    '& td': { verticalAlign: 'middle', py: 0.5, px: 1 },
  };

  const inputSlotPadding = { sx: { py: '10px', px: '12px', minHeight: 40, boxSizing: 'border-box' as const } };
  const textareaSlotPadding = { sx: { py: '10px', px: '12px', minHeight: 40, maxHeight: 72, overflowY: 'auto' } };

  return (
    <Box sx={{ p: 2, maxWidth: 'min(100%, 960px)', mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 2 }}>
        Creación masiva de categorías
      </Typography>

      {showResults && (
        <Alert
          color={results.errors === 0 ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          endDecorator={
            <Button size="sm" onClick={() => navigate('/categories')}>
              Ver todas las categorías
            </Button>
          }
        >
          <Typography level="title-sm">Resultados</Typography>
          <Typography level="body-sm">{results.success} categorías creadas correctamente</Typography>
          {results.errors > 0 ? (
            <Typography level="body-sm">{results.errors} con errores</Typography>
          ) : null}
        </Alert>
      )}

      <Sheet variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography level="title-md">Importar desde CSV</Typography>
              <Button size="sm" variant="plain" color="primary" onClick={downloadTemplate}>
                Descargar plantilla
              </Button>
            </Stack>
            <Button component="label" variant="outlined" color="neutral">
              Elegir archivo CSV
              <input type="file" accept=".csv" hidden onChange={handleImportFromCsv} />
            </Button>
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
              Formato: Nombre, Descripción (primera fila puede ser encabezado)
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Edición en tabla
            </Typography>
            <Box
              sx={{
                overflowX: 'auto',
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: 'min(70vh, 720px)',
                overflowY: 'auto',
              }}
            >
              <Table stickyHeader size="sm" borderAxis="bothBetween" sx={bulkTableSx}>
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col style={{ width: '32%' }} />
                  <col />
                  <col style={{ width: 52 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th aria-label="Eliminar" />
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <Typography level="body-sm" sx={{ color: 'neutral.500', py: 2, textAlign: 'center' }}>
                          No hay filas. Usá «Agregar otra fila» para comenzar.
                        </Typography>
                      </td>
                    </tr>
                  ) : null}
                  {categories.map((category, index) => (
                    <tr key={index}>
                      <td style={{ textAlign: 'center' }}>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {index + 1}
                        </Typography>
                      </td>
                      <td>
                        <Input
                          size="sm"
                          variant="soft"
                          color="neutral"
                          placeholder="Nombre"
                          value={category.name}
                          onChange={(e) => updateCategory(index, { name: e.target.value })}
                          slotProps={{ input: inputSlotPadding }}
                          sx={{ width: '100%', fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        <Textarea
                          size="sm"
                          variant="soft"
                          color="neutral"
                          minRows={1}
                          maxRows={3}
                          placeholder="Descripción"
                          value={category.description}
                          onChange={(e) => updateCategory(index, { description: e.target.value })}
                          slotProps={{ textarea: textareaSlotPadding }}
                          sx={{ width: '100%', fontSize: 'sm' }}
                        />
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <IconButton
                          type="button"
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemoveCategory(index)}
                          aria-label="Eliminar fila"
                        >
                          <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>

            <Button type="button" variant="outlined" color="neutral" onClick={handleAddRow} sx={{ mt: 1.5 }}>
              + Agregar otra fila
            </Button>

            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 2 }}>
              <Button type="submit" color="primary" loading={loading}>
                Crear categorías
              </Button>
              <Button type="button" variant="outlined" color="neutral" onClick={() => navigate('/categories')}>
                Cancelar
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Sheet>
    </Box>
  );
};

export default BulkCategoryCreator;
